import React, { useState } from 'react';
import { Sidebar } from './Sidebar';
import { LayoutDashboard, UserPlus, Users, Trophy } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import schoolLogo from '../../assets/school-logo-header.png';

interface SchoolLayoutProps {
    children: React.ReactNode;
}

export const SchoolLayout: React.FC<SchoolLayoutProps> = ({ children }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { logout, user, school } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Failed to logout', error);
        }
    };

    const menuItems = [
        { name: 'Dashboard', path: '/school/dashboard', icon: <LayoutDashboard size={20} /> },
        { name: 'Register Participants', path: '/school/register', icon: <UserPlus size={20} /> },
        { name: 'My Team', path: '/school/team', icon: <Users size={20} /> },
        { name: 'Results', path: '/school/results', icon: <Trophy size={20} /> },
    ];

    return (
        <div className="min-h-screen bg-[#0A0A0A] flex flex-col md:flex-row">
            {/* Mobile Header */}
            <div className="md:hidden h-16 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/[0.06] flex items-center justify-between px-4 sticky top-0 z-50">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/[0.03] border border-white/[0.08] flex items-center justify-center overflow-hidden">
                        <img src={schoolLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                    </div>
                    <span className="font-display font-bold text-white tracking-wide">School Portal</span>
                </div>
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 text-gray-400 hover:text-white"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {isMobileMenuOpen ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        )}
                    </svg>
                </button>
            </div>

            {/* Desktop Sidebar */}
            <div className="hidden md:block">
                <Sidebar
                    items={menuItems}
                    isOpen={isSidebarOpen}
                    onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
                    onLogout={handleLogout}
                    title="School Portal"
                    userEmail={school?.school_name || user?.email || 'School Rep'}
                />
            </div>

            {/* Mobile Drawer */}
            <div className={`md:hidden fixed inset-0 z-40 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
                <Sidebar
                    items={menuItems}
                    isOpen={isMobileMenuOpen}
                    onToggle={() => setIsMobileMenuOpen(false)}
                    onLogout={handleLogout}
                    title="School Portal"
                    userEmail={school?.school_name || user?.email || 'School Rep'}
                    mobile={true}
                />
            </div>

            <main
                className={`
          flex-grow transition-all duration-300 min-h-screen relative
          ml-0 ${isSidebarOpen ? 'md:ml-80' : 'md:ml-28'}
        `}
            >
                {/* Background Grid */}
                <div className="fixed inset-0 bg-cyber-grid opacity-[0.02] pointer-events-none z-0"></div>

                <div className="relative z-10 p-4 md:p-8 pt-6 md:pt-8">
                    {children}
                </div>
            </main>
        </div>
    );
};
